import { useState, useMemo, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell,
  PieChart, Pie, Legend,
} from 'recharts';
import { Download, ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import InfoTooltip from './InfoTooltip';

// ── Types ──
interface DataItem {
  name: string;
  subLabel?: string;
  totalRevenue: number;
  orderCount: number;
}

interface Props {
  isDark: boolean;
  title: string;
  tooltipText: string;
  data: DataItem[];
  reportFrom: string;
  reportTo: string;
  csvFileName: string;
  colors?: string[];
  /** Optional extra column shown after name in table view (e.g. "Tỉnh thành") */
  extraColumn?: { label: string; field: 'subLabel' };
  /** Hide the totalRevenue column (useful when revenue is always 0) */
  hideRevenue?: boolean;
  /** Custom label for orderCount column (default: "Số đơn") */
  orderCountLabel?: string;
}

// ── Palette ──
const DEFAULT_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#a855f7',
  '#6b7280', '#84cc16', '#e11d48', '#0ea5e9', '#d946ef',
];

function formatVND(value: number): string {
  return value.toLocaleString('vi-VN');
}

// ── View toggle ──
type ViewMode = 'table' | 'column' | 'pie';

const VIEW_LABELS: Record<ViewMode, string> = {
  table: 'Bảng',
  column: 'Cột',
  pie: 'Tỷ trọng',
};

// ── Sort ──
type SortKey = 'name' | 'subLabel' | 'totalRevenue' | 'orderCount' | 'pct';
type SortDir = 'asc' | 'desc';

function SortHeader({ label, sortKey, currentSort, currentDir, onSort, isDark, align }: {
  label: string; sortKey: SortKey; currentSort: SortKey; currentDir: SortDir;
  onSort: (k: SortKey) => void; isDark: boolean; align?: 'left' | 'right';
}) {
  const active = currentSort === sortKey;
  return (
    <th
      className={`py-2.5 px-3 font-semibold text-xs uppercase tracking-wider cursor-pointer select-none group ${align === 'right' ? 'text-right' : 'text-left'} ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-500 hover:text-gray-900'}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (currentDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ChevronsUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50" />}
      </span>
    </th>
  );
}

export default function RevenueBreakdown({ isDark, title, tooltipText, data, reportFrom, reportTo, csvFileName, colors, extraColumn, hideRevenue, orderCountLabel }: Props) {
  const palette = colors || DEFAULT_COLORS;
  const [view, setView] = useState<ViewMode>('table');
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [sortKey, setSortKey] = useState<SortKey>(hideRevenue ? 'orderCount' : 'totalRevenue');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
  };

  const { sorted, grandTotal, grandOrders } = useMemo(() => {
    const grandTotal = data.reduce((s, r) => s + r.totalRevenue, 0);
    const grandOrders = data.reduce((s, r) => s + r.orderCount, 0);
    const sorted = [...data].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'subLabel': cmp = (a.subLabel || '').localeCompare(b.subLabel || ''); break;
        case 'totalRevenue': case 'pct': cmp = a.totalRevenue - b.totalRevenue; break;
        case 'orderCount': cmp = a.orderCount - b.orderCount; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return { sorted, grandTotal, grandOrders };
  }, [data, sortKey, sortDir]);

  const exportCSV = () => {
    const header = extraColumn ? `#,Tên,${extraColumn.label},Doanh thu,Số đơn,Tỷ lệ (%)` : '#,Tên,Doanh thu,Số đơn,Tỷ lệ (%)';
    const rows = [header];
    sorted.forEach((r, i) => {
      const pct = grandTotal > 0 ? ((r.totalRevenue / grandTotal) * 100).toFixed(1) : '0.0';
      const extra = extraColumn ? `,"${(r.subLabel || '').replace(/"/g, '""')}"` : '';
      rows.push(`${i + 1},"${r.name.replace(/"/g, '""')}"${extra},${r.totalRevenue},${r.orderCount},${pct}`);
    });
    const extraEmpty = extraColumn ? ',' : '';
    rows.push(`${extraEmpty},TỔNG,${grandTotal},${grandOrders},100.0`);
    const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${csvFileName}_${reportFrom}_${reportTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleClass = (active: boolean) =>
    `px-2.5 py-1 text-xs font-medium rounded transition-colors ${active ? 'bg-cyan-500 text-white' : isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`;

  const chartTooltipStyle = {
    contentStyle: { backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`, borderRadius: '8px', color: isDark ? '#f8fafc' : '#111827' },
    labelStyle: { color: isDark ? '#f8fafc' : '#111827', fontWeight: 600 },
  };

  if (!data.length) {
    return (
      <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {title}<InfoTooltip isDark={isDark} text={tooltipText} />
        </h3>
        <div className={`flex items-center justify-center h-48 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Chưa có dữ liệu</div>
      </div>
    );
  }

  return (
    <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {title}<InfoTooltip isDark={isDark} text={tooltipText} />
          {' '}
          <span className={`text-sm font-normal ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>({reportFrom} — {reportTo})</span>
        </h3>
        <div className="flex-1" />
        <div className={`inline-flex rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          {(['table', 'column', 'pie'] as ViewMode[]).map(v => (
            <button key={v} onClick={() => setView(v)} className={toggleClass(view === v)}>{VIEW_LABELS[v]}</button>
          ))}
        </div>
        <button onClick={exportCSV} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`}>
          <Download className="w-3.5 h-3.5" />CSV
        </button>
      </div>

      {/* ── TABLE ── */}
      {view === 'table' && (
        <div className="overflow-x-auto mt-3">
          <table className={`w-full text-sm ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>
            <thead>
              <tr className={isDark ? 'border-b border-white/10' : 'border-b border-gray-200'}>
                <th className={`text-left py-2.5 px-3 font-semibold text-xs uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>#</th>
                <SortHeader label="Tên" sortKey="name" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} isDark={isDark} />
                {extraColumn && <SortHeader label={extraColumn.label} sortKey="subLabel" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} isDark={isDark} />}
                {!hideRevenue && <SortHeader label="Doanh thu" sortKey="totalRevenue" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} isDark={isDark} align="right" />}
                <SortHeader label={orderCountLabel || "Số đơn"} sortKey="orderCount" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} isDark={isDark} align="right" />
                <SortHeader label="Tỷ lệ" sortKey="pct" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} isDark={isDark} align="right" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => {
                const pct = hideRevenue
                  ? (grandOrders > 0 ? (row.orderCount / grandOrders) * 100 : 0)
                  : (grandTotal > 0 ? (row.totalRevenue / grandTotal) * 100 : 0);
                return (
                  <tr key={row.name} className={`${isDark ? 'border-b border-white/5 hover:bg-white/5' : 'border-b border-gray-100 hover:bg-gray-50'} transition-colors`}>
                    <td className={`py-2 px-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{i + 1}</td>
                    <td className="py-2 px-3 font-medium">{row.name}</td>
                    {extraColumn && <td className={`py-2 px-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{row.subLabel}</td>}
                    {!hideRevenue && <td className="py-2 px-3 text-right font-mono font-medium">{formatVND(row.totalRevenue)}</td>}
                    <td className={`py-2 px-3 text-right ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{formatVND(row.orderCount)}</td>
                    <td className="py-2 px-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className={`w-14 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                          <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: palette[i % palette.length] }} />
                        </div>
                        <span className={`text-xs w-12 text-right ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{pct.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className={`font-bold ${isDark ? 'border-t border-white/20 text-white' : 'border-t-2 border-gray-300 text-gray-900'}`}>
                <td className="py-2.5 px-3" />
                <td className="py-2.5 px-3">TỔNG</td>
                {extraColumn && <td className="py-2.5 px-3" />}
                {!hideRevenue && <td className="py-2.5 px-3 text-right font-mono">{formatVND(grandTotal)}</td>}
                <td className={`py-2.5 px-3 text-right ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{formatVND(grandOrders)}</td>
                <td className={`py-2.5 px-3 text-right text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>100.0%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ── COLUMN CHART ── */}
      {view === 'column' && (
        <ResponsiveContainer width="100%" height={Math.max(280, sorted.length * 40 + 60)}>
          <BarChart data={sorted} layout="vertical" margin={{ top: 10, right: isMobile ? 48 : 100, left: 4, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} horizontal={false} />
            <XAxis type="number" tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }} tickFormatter={formatVND} />
            <YAxis type="category" dataKey="name" width={isMobile ? 90 : 160} tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: isMobile ? 10 : 12 }} />
            <Tooltip {...chartTooltipStyle} formatter={(value: any) => [formatVND(value as number) + (hideRevenue ? '' : ' VND'), hideRevenue ? (orderCountLabel || 'Số lượng') : 'Doanh thu']} />
            <Bar dataKey={hideRevenue ? 'orderCount' : 'totalRevenue'} radius={[0, 4, 4, 0]} barSize={isMobile ? 20 : 28}>
              {sorted.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
              <LabelList dataKey={hideRevenue ? 'orderCount' : 'totalRevenue'} position="right" formatter={(v: any) => formatVND(v as number)} style={{ fill: isDark ? '#cbd5e1' : '#374151', fontSize: isMobile ? 9 : 11, fontWeight: 500 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}

      {/* ── PIE CHART ── */}
      {view === 'pie' && (
        <ResponsiveContainer key="pie" width="100%" height={400}>
          <PieChart>
            <Pie
              data={sorted.map(r => ({ ...r }))}
              dataKey={hideRevenue ? 'orderCount' : 'totalRevenue'}
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={120}
              isAnimationActive={false}
              label={(props: any) => {
                const base = hideRevenue ? grandOrders : grandTotal;
                const val = hideRevenue ? props.orderCount : props.totalRevenue;
                const pct = base > 0 ? ((val / base) * 100).toFixed(1) : '0';
                return `${props.name}: ${pct}%`;
              }}
              labelLine
            >
              {sorted.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
            </Pie>
            <Tooltip {...chartTooltipStyle} formatter={(value: any) => [formatVND(value as number) + (hideRevenue ? '' : ' VND'), hideRevenue ? (orderCountLabel || 'Số lượng') : 'Doanh thu']} />
            <Legend wrapperStyle={{ color: isDark ? '#cbd5e1' : '#374151', fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
